import { HotelRoomPriceOrdering } from '@shared/models/hotel-search-response.model';

export interface ISiteListDetails {
  country_code: string;
  display_country_code: string;
  language: string;
  site_id: number;
  display_name: string;
  domain: string;
  bidi: boolean;
}

export interface documentation_url {
  title: string;
  link: string;
}

export interface ISiteSettings {
  authentication_provider: IAuthenticationProvider;
  advance_booking_window: string;
  client: { name: string };
  contact_numbers: ISiteSettingsContactNumbers;
  cookie_setting_id: string;
  country_code_alpha3: string;
  country_code: string;
  covid_entry_point_display_format: string;
  currency: string;
  domain: string;
  eligibilities_and_selfregs: string;
  eligibility_pan_length: number;
  fe_configuration: ISiteConfigSettings;
  genesys_widget_key: string;
  genesys_web_messenger_deployment_id: string;
  global_travel_value_proposition: string;
  header_external_portal_hyperlink: string;
  hotel_ordering_level?: IHotelOrderingLevel;
  hotel_room_ordering_level?: IHotelRoomOrderingLevel;
  id: number;
  language: string;
  main_country: string;
  mtls_priceless_planet_script: string;
  name: string;
  product_group_ordering: IProductGroupOrdering[];
  room_level_settings?: any;
  scripts: string;
  session_timeout_in_minutes?: number;
  switches: ISiteSettingsSwitches;
  travel_services: TravelService[];
  use_payment_service: boolean;
  welcome_video_url: string;
  workgroup: string;
  documentation_url: documentation_url;
  country_latitude: string;
  country_longitude: string;
}

export interface TravelService {
  contact: string;
  contact_type: 'phone' | 'email';
  label: string;
  label_icon: string;
  popup_body: string;
  popup_body_icon: string;
  popup_header: string;
}

export interface IHotelOrderingLevel {
  second_level_sort_by: SortBy.PRICE | SortBy.RATING;
  second_level_sort_type: SortType.ASC | SortType.DES;
  third_level_sort_by: SortBy.PRICE | SortBy.RATING;
  third_level_sort_type: SortType.ASC | SortType.DES;
}

export interface IHotelRoomOrderingLevel {
  sort_by_price_ascending: boolean;
  product_group: IRoomGroupOrder[];
}

export interface IRoomGroupOrder {
  product_group: string;
  product_code: string;
  priority: number;
}

export interface IRoomPriceOrderingType {
  ordering: HotelRoomPriceOrdering;
  poTag: string;
}

export enum SortDirection {
  AB = '(a,b)',
  BA = '(b,a)',
}
export enum SortType {
  ASC = 'ascending',
  DES = 'descending',
}
export enum SortBy {
  RATING = 'rating',
  PRICE = 'price',
}

export interface IProductGroupOrdering {
  product_group: string;
  priority: number;
  product_code: string;
  banner: IBannerProductGroupOrdering;
  room_level_banner: boolean;
}

export interface IBannerProductGroupOrdering {
  key: string;
  value: string;
}

export interface ISiteConfigSettings {
  ghostry_cookie_consent_script_active: boolean;
  hide_links_to_app_features: boolean;
  meta_description?: string;
  meta_keywords?: string;
  name: string;
  path: string;
  platform_provider_type: PlatformProviderType;
  root_route: PlatformRootRoute;
  segment_script_id: string;
  self_registration: boolean;
  show_homepage_video: boolean;
  show_platform_provider_footer: boolean;
  show_platform_provider_nav: boolean;
  single_feature_brand: boolean;
}

export enum PlatformProviderType {
  BLANK = '',
  PROVIDER = 'provider',
  DELIVERED = 'delivered',
}

export enum PlatformRootRoute {
  LANDING = 'landing',
  TRAVEL = 'travel',
  REQUEST_MANAGEMENT = 'request-management',
}

export interface ISiteSettingsContactNumbers {
  dining: string;
  entertainment: string;
  general: string;
  member_benefits: string;
  member_events: string;
  support: string;
  travel: string;
}

export interface ISiteSettingsSwitches {
  accept_bin_for_eligibility: boolean;
  access_to_HTR: boolean;
  access_to_webchat: any;
  authentication_coutts_sso: boolean;
  authentication_sso: boolean;
  auto_invite_new_member: boolean;
  checked_bags_tool_tip: boolean;
  client_restrict_contacts_my_profile: boolean;
  client_restrict_contacts_request_and_booking_summary: boolean;
  concierge_briefing: boolean;
  contact_details: boolean;
  custom_footer_design: boolean;
  delegate_members: boolean;
  details_address: boolean;
  details_member_profile: boolean;
  details_phone_number: boolean;
  dining_auto_translate: boolean;
  dining_booking_recaptcha: boolean;
  dining_landing_show_local_content_only: boolean;
  dining_module: boolean;
  dining_oss_phase_1: boolean;
  dining_oss_phase_2: boolean;
  dining_request_recaptcha: boolean;
  dining_third_party_integrations: boolean;
  display_installments_contact_banner: boolean;
  emergency_banner_entertainment: boolean;
  emergency_banner_homepage: boolean;
  emergency_banner_travel: boolean;
  enable_centurion_benefits: boolean;
  enable_centurion_card_design_selector: boolean;
  enable_cpf_check: boolean;
  enable_member_acquisition_homepage: boolean;
  enable_mtls_priceless_planet_widget: boolean;
  enable_pay_with_points: boolean;
  enable_pwp_hotels_path: boolean;
  enable_recaptchas: boolean;
  enable_referral_rock_script: boolean;
  enable_eligibility_recaptcha: boolean;
  enable_registration_recaptcha: boolean;
  enable_email_change_recaptcha: boolean;
  enable_adyen_instalments: boolean;
  entertainment_booking_recaptcha: boolean;
  entertainment_module: boolean;
  entertainment_request_recaptcha: boolean;
  entertainment_search_show_local_content_only: boolean;
  entertainment_redesign_phase_1: boolean;
  entertainment_redesign_phase_1_1: boolean;
  entertainment_redesign_phase_2: boolean;
  entertainment_redesign_phase_3: boolean;
  inspiration_module: boolean;
  is_sharia_compliant: boolean;
  limousine_v2: boolean;
  member_activity_covid_support_points: boolean;
  member_benefit_request_recaptcha: boolean;
  member_benefits_module: boolean;
  member_events_booking_recaptcha: boolean;
  member_events_module: boolean;
  member_status_endpoint: boolean;
  newsletter_notifications: boolean;
  partners_module: boolean;
  payment_cards: boolean;
  payment_ingenico: boolean;
  preferences_dining: boolean;
  preferences_artist: boolean;
  preferences_member_benefits: boolean;
  preferences_interests: boolean;
  preferences_page: boolean;
  preferences_travel: boolean;
  product_dropdown: boolean;
  require_address_on_registration: boolean;
  show_phone_number_on_registration_form: boolean;
  show_date_of_birth_on_registration_form: boolean;
  require_date_of_birth_on_registration_form: boolean;
  require_phone_number_as_mandatory: boolean;
  requests_module: boolean;
  request_management_feature: boolean;
  self_registration: boolean;
  send_member_benefit_confirmation_emails: boolean;
  show_address_on_registration: boolean;
  show_chat_phone_number: boolean;
  show_country_language_selector_page: boolean;
  pre_login_home_page: boolean;
  pre_login_benefits_page: boolean;
  pre_login_inspiration_page: boolean;
  show_genesys_web_messenger: boolean;
  show_hotel_details_page_message: boolean;
  show_registration_result_in_popup: boolean;
  show_tour: boolean;
  sitata_travel_search_widget: boolean;
  stored_cards: boolean;
  travel_car_hire_module: boolean;
  travel_covid_support_points: boolean;
  travel_fare_families: boolean;
  travel_flights_expedited_immigration_request: boolean;
  travel_flights_limousine_request: boolean;
  travel_flights_local_carriers: boolean;
  travel_flights_module: boolean;
  travel_hotels_module: boolean;
  travel_hotels_rooms_price_per_night: boolean;
  travel_module: boolean;
  travellers_frequent_flyer: boolean;
  travellers_hotel_loyalty: boolean;
  travellers: boolean;
  use_car_hire_payment_instalments: boolean;
  preferences_follow_events_and_entertainment: boolean;
  travel_activities_module: boolean;
  show_marketing_opt_in_prompt: boolean;
  hotel_search_results_redesign: boolean;
  change_marketing_consent_on_platform: boolean;
  enable_wootric_online_survey: boolean;
}

export interface IAuthenticationProvider {
  provider: string;
  login_url: string;
}
